<script setup lang="ts">
import { computed } from 'vue'
import { Slider } from '@/components/ui/slider'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { CoordinateInput } from '@/components/ui/coordinate-input'
import ColorPicker from '@/components/ui/color-picker/ColorPicker.vue'
import ButtonSelector from '@/components/ui/button-selector/ButtonSelector.vue'
import Button from '@/components/ui/button/Button.vue'
import { BezierPicker } from '@/components/ui/bezier-picker'
import type { EasingName } from '@/components/ui/bezier-picker'
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent
} from '@/components/ui/accordion'
import type { ConfigControlsSchema, ControlSchema, ControlOption } from '@/stores/viewConfig'

const props = defineProps<{
  schema: ConfigControlsSchema
  getValue: (path: string) => any
  onUpdate: (path: string, value: any) => void
  basePath?: string
  onAction?: (name: string) => void
  nested?: boolean
}>()

const isControlSchema = (object: any): object is ControlSchema => {
  if (typeof object !== 'object' || object === null || Array.isArray(object)) return false
  const keys = Object.keys(object)
  const controlKeys = [
    'min',
    'max',
    'step',
    'boolean',
    'checkbox',
    'color',
    'bezier',
    'file',
    'label',
    'options',
    'component',
    'direction',
    'callback',
    'sectionStart',
    'queryParam'
  ]

  // Empty object is a control schema (will be rendered as input)
  if (keys.length === 0) return true

  // If ALL keys are control keys AND at least one is a control property (not nested config), it's a control schema
  // Check if values are primitive types (number, string, boolean) or arrays for options
  const allKeysAreControlKeys = keys.every((k) => controlKeys.includes(k))
  if (!allKeysAreControlKeys) return false

  // If all keys are control keys, check if any value is an object (meaning it's nested config)
  // BUT allow min/max/step to be objects (for per-coordinate values in CoordinateInput)
  const hasNestedObjects = keys.some((k) => {
    if (k === 'min' || k === 'max' || k === 'step') return false // Allow these to be objects
    const value = object[k]
    return typeof value === 'object' && value !== null && !Array.isArray(value)
  })

  // It's a control schema only if all keys are control keys AND there are no nested objects
  return !hasNestedObjects
}

const formatLabel = (key: string): string => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (string_) => string_.toUpperCase())
    .trim()
}

const getFullPath = (key: string): string => {
  return props.basePath ? `${props.basePath}.${key}` : key
}

const groups = computed(() => {
  const result: { key: string; schema: ConfigControlsSchema }[] = []
  const controls: { key: string; path: string; schema: ControlSchema }[] = []

  Object.entries(props.schema).forEach(([key, value]) => {
    if (key.startsWith('__')) return
    if (isControlSchema(value)) {
      controls.push({ key, path: getFullPath(key), schema: value })
    } else {
      result.push({ key, schema: value as ConfigControlsSchema })
    }
  })

  return { groups: result, controls }
})

const defaultOpenGroups = computed(() => {
  const meta = (props.schema as Record<string, unknown>)['__defaultOpenGroups']
  if (!props.basePath && Array.isArray(meta)) return meta as string[]
  return groups.value.groups.map((g) => g.key)
})

const handleSliderUpdate = (path: string, value: number[]) => {
  props.onUpdate(path, value[0])
}

const handleInputUpdate = (path: string, value: string | number) => {
  const numberValue = typeof value === 'string' ? parseFloat(value) : value
  if (!isNaN(numberValue)) {
    props.onUpdate(path, numberValue)
  }
}

const handleCheckboxUpdate = (path: string, value: boolean) => {
  props.onUpdate(path, value)
}

const handleFileUpdate = (path: string, event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  props.onUpdate(path, URL.createObjectURL(file))
}

const handleSelectUpdate = (path: string, value: string) => {
  props.onUpdate(path, value)
}

const handleColorUpdate = (path: string, hexValue: string) => {
  // Convert hex to integer (e.g., #98887d -> 0x98887d)
  const colorInt = parseInt(hexValue.replace('#', ''), 16)
  props.onUpdate(path, colorInt)
}

const getColorHex = (path: string): string => {
  const value = props.getValue(path)
  if (typeof value === 'number') {
    // Convert integer to hex string (e.g., 0x98887d -> #98887d)
    return `#${value.toString(16).padStart(6, '0')}`
  }
  return value || '#000000'
}

const isButtonSelector = (schema: ControlSchema): boolean => schema.component === 'ButtonSelector'

const isCallback = (schema: ControlSchema): boolean => schema.callback !== undefined

const normalizeOptions = (options: string[] | ControlOption[]): ControlOption[] =>
  options.map((opt) => (typeof opt === 'string' ? { value: opt, label: opt } : opt))

const handleButtonSelectorUpdate = (path: string, value: string) => {
  props.onUpdate(path, value)
}
</script>

<template>
  <!-- Nested groups drop the gap: their own controls already carry spacing, and
       stacking both leaves a part's fields further from each other than from
       the next part's. -->
  <div class="config-controls flex flex-col" :class="{ 'gap-2': !nested }">
    <!-- Direct controls at this level -->
    <div
      v-for="control in groups.controls"
      :key="control.path"
      class="config-controls__item flex flex-col gap-1"
      :class="{ 'config-controls__item--section': control.schema.sectionStart }"
    >
      <template v-if="isCallback(control.schema)">
        <Button
          size="sm"
          variant="secondary"
          class="w-full"
          @click="onAction?.(control.schema.callback!)"
        >
          {{ control.schema.label ?? formatLabel(control.key) }}
        </Button>
      </template>

      <template v-else-if="control.schema.component === 'CoordinateInput'">
        <CoordinateInput
          :model-value="getValue(control.path)"
          :label="control.schema.label ?? formatLabel(control.key)"
          :min="control.schema.min"
          :max="control.schema.max"
          :step="control.schema.step"
          @update:model-value="onUpdate(control.path, $event)"
        />
      </template>

      <template
        v-else-if="control.schema.options !== undefined && isButtonSelector(control.schema)"
      >
        <label class="text-xs font-medium">
          {{ control.schema.label ?? formatLabel(control.key) }}
        </label>
        <ButtonSelector
          :model-value="String(getValue(control.path) || '')"
          :options="normalizeOptions(control.schema.options)"
          :direction="control.schema.direction"
          @update:model-value="handleButtonSelectorUpdate(control.path, $event)"
        />
      </template>

      <template v-else-if="control.schema.options !== undefined">
        <label :for="control.path" class="text-xs font-medium">
          {{ control.schema.label ?? formatLabel(control.key) }}
        </label>
        <Select
          :model-value="String(getValue(control.path) || '')"
          :options="normalizeOptions(control.schema.options)"
          @update:model-value="handleSelectUpdate(control.path, $event)"
          class="h-7 text-xs"
        />
      </template>

      <template v-else-if="control.schema.color !== undefined">
        <div class="config-controls__color-row">
          <label :for="control.path" class="text-xs font-medium">
            {{ control.schema.label ?? formatLabel(control.key) }}
          </label>
          <ColorPicker
            :model-value="getColorHex(control.path)"
            @update:model-value="handleColorUpdate(control.path, $event)"
          />
        </div>
      </template>

      <template v-else-if="control.schema.checkbox !== undefined">
        <div class="flex items-center gap-2">
          <input
            :id="control.path"
            type="checkbox"
            :checked="getValue(control.path) ?? control.schema.checkbox"
            class="config-controls__checkbox"
            @change="
              handleCheckboxUpdate(control.path, ($event.target as HTMLInputElement).checked)
            "
          />
          <label :for="control.path" class="text-xs font-medium cursor-pointer">
            {{ control.schema.label ?? formatLabel(control.key) }}
          </label>
        </div>
      </template>

      <template v-else-if="control.schema.boolean !== undefined">
        <div class="flex items-center gap-2">
          <input
            :id="control.path"
            type="checkbox"
            :checked="getValue(control.path) ?? control.schema.boolean"
            class="config-controls__checkbox"
            @change="
              handleCheckboxUpdate(control.path, ($event.target as HTMLInputElement).checked)
            "
          />
          <label :for="control.path" class="text-xs font-medium cursor-pointer">
            {{ control.schema.label ?? formatLabel(control.key) }}
          </label>
        </div>
      </template>

      <template v-else-if="control.schema.bezier !== undefined">
        <label class="text-xs font-medium">
          {{ control.schema.label ?? formatLabel(control.key) }}
        </label>
        <BezierPicker
          :model-value="(getValue(control.path) as EasingName) ?? 'linear'"
          @update:model-value="onUpdate(control.path, $event)"
        />
      </template>

      <template v-else-if="control.schema.file !== undefined">
        <label class="text-xs font-medium config-controls__file-label">
          {{ control.schema.label ?? formatLabel(control.key) }}
          <div class="config-controls__file-row">
            <img
              v-if="getValue(control.path)"
              :src="getValue(control.path)"
              class="config-controls__file-preview"
              alt="Texture preview"
            />
            <input
              type="file"
              :accept="control.schema.file"
              class="config-controls__file-input"
              @change="handleFileUpdate(control.path, $event)"
            />
          </div>
        </label>
      </template>

      <template v-else-if="control.schema.min !== undefined || control.schema.max !== undefined">
        <label :for="control.path" class="text-xs font-medium">
          {{ control.schema.label ?? formatLabel(control.key) }}:
          <input
            type="number"
            :value="getValue(control.path) ?? 0"
            :min="typeof control.schema.min === 'number' ? control.schema.min : 0"
            :max="typeof control.schema.max === 'number' ? control.schema.max : 100"
            :step="typeof control.schema.step === 'number' ? control.schema.step : 1"
            @input="handleInputUpdate(control.path, ($event.target as HTMLInputElement).value)"
            class="config-controls__inline-input"
          />
        </label>
        <Slider
          :id="control.path"
          :model-value="[
            getValue(control.path) ??
              (typeof control.schema.min === 'number' ? control.schema.min : 0)
          ]"
          :min="typeof control.schema.min === 'number' ? control.schema.min : 0"
          :max="typeof control.schema.max === 'number' ? control.schema.max : 100"
          :step="typeof control.schema.step === 'number' ? control.schema.step : 1"
          @update:model-value="handleSliderUpdate(control.path, $event)"
        />
      </template>

      <template v-else>
        <label :for="control.path" class="text-xs font-medium">
          {{ control.schema.label ?? formatLabel(control.key) }}
        </label>
        <Input
          :id="control.path"
          type="number"
          :model-value="getValue(control.path) ?? 0"
          :step="typeof control.schema.step === 'number' ? control.schema.step : undefined"
          class="h-7 text-xs"
          @update:model-value="handleInputUpdate(control.path, $event)"
        />
      </template>
    </div>

    <!-- Sub-groups: accordion at top level, plain sections when already nested -->
    <template v-if="groups.groups.length > 0">
      <template v-if="nested">
        <div v-for="group in groups.groups" :key="group.key" class="config-controls__section">
          <div
            class="config-controls__section-label text-xs font-medium mt-2 mb-1 text-muted-foreground"
          >
            {{ formatLabel(group.key) }}
          </div>
          <ConfigControls
            :schema="group.schema"
            :get-value="getValue"
            :on-update="onUpdate"
            :base-path="getFullPath(group.key)"
            :on-action="onAction"
            nested
          />
        </div>
      </template>
      <Accordion v-else type="multiple" :default-value="defaultOpenGroups" class="w-full">
        <AccordionItem v-for="group in groups.groups" :key="group.key" :value="group.key">
          <AccordionTrigger class="text-xs font-medium py-1">
            {{ formatLabel(group.key) }}
          </AccordionTrigger>
          <AccordionContent>
            <ConfigControls
              :schema="group.schema"
              :get-value="getValue"
              :on-update="onUpdate"
              :base-path="getFullPath(group.key)"
              :on-action="onAction"
              nested
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </template>
  </div>
</template>

<style scoped>
/* No `overflow-x: hidden` here: when one axis is hidden the other computes to
   `auto`, which turned this into a vertical scroll container and clipped the
   last control of every group. `min-width: 0` keeps wide rows from stretching
   the panel without creating a scroll context. */
.config-controls {
  min-width: 0;
  padding-bottom: 0.25rem;
}

.config-controls__inline-input {
  background: transparent;
  border: none;
  color: hsl(var(--muted-foreground));
  font: inherit;
  text-align: left;
  width: 3.5em;
  padding: 0 4px;
  outline: none;
  border-radius: 2px;
  transition: background-color 0.15s;
  font-size: inherit;
  font-weight: inherit;
}

.config-controls__inline-input:hover {
  background-color: rgb(255, 255, 255, 0.05);
}

.config-controls__inline-input:focus {
  background-color: rgb(255, 255, 255, 0.1);
  box-shadow: 0 0 0 1px rgb(255, 255, 255, 0.2);
}

/* Hide spinner buttons in number input */
.config-controls__inline-input::-webkit-outer-spin-button,
.config-controls__inline-input::-webkit-inner-spin-button {
  appearance: none;
  margin: 0;
}

.config-controls__inline-input[type='number'] {
  appearance: textfield;
}

.config-controls__color-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
}

.config-controls__item--section {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid hsl(var(--border));
}

.config-controls__checkbox {
  width: 0.875rem;
  height: 0.875rem;
  cursor: pointer;
  accent-color: hsl(var(--primary));
  flex-shrink: 0;
}

.config-controls__file-label {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1, 0.25rem);
  cursor: pointer;
}

.config-controls__file-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-1, 0.25rem);
}

.config-controls__file-preview {
  width: 1.75rem;
  height: 1.75rem;
  flex-shrink: 0;
  object-fit: cover;
  border: 1px solid hsl(var(--border));
  border-radius: 0.25rem;
}

.config-controls__file-input {
  font-size: 0.625rem;
  color: hsl(var(--muted-foreground));
  cursor: pointer;
}

.config-controls__file-input::file-selector-button {
  font-size: 0.625rem;
  padding: 0.125rem 0.375rem;
  border: 1px solid hsl(var(--border));
  border-radius: 0.25rem;
  background: hsl(var(--secondary));
  color: hsl(var(--secondary-foreground));
  cursor: pointer;
}
</style>
